import React from "react"
import { Route, Switch, useRouteMatch } from "react-router-dom"
import {
  AccountUser, DioboxEvent, StripeAccount, User,
} from "sharedTypes"
import { Flex } from "@chakra-ui/react"
import CenteredContent from "components/layout/CenteredContent"
import SecondaryNav from "components/layout/SecondaryNav"
import BadgePrinting from "pages/EventShow/Settings/BadgePrinting"
import CustomDomains from "pages/EventShow/Settings/CustomDomains"
import EmailNotifications from "pages/EventShow/Settings/EmailNotifications"
import EmbedCode from "pages/EventShow/Settings/EmbedCode"
import EventActivities from "pages/EventShow/Settings/EventActivities"
import General from "pages/EventShow/Settings/General"
import QRCodes from "pages/EventShow/Settings/QRCodes/QRCodes"
import TeamMembers from "pages/EventShow/Settings/TeamMembers"
import TicketingPayments from "pages/EventShow/Settings/TicketingPayments"
import useFeatureFlag from "services/featureFlags"
import useFeatures from "services/useFeatures"
import KioskMode from "pages/EventShow/Settings/KioskMode"
import WalletPass from "pages/EventShow/Settings/WalletPass"
import WalletPassBuilder from "components/WalletPass/WalletPassBuilder"
import LanguageTemplates from "pages/EventShow/Settings/LanguageTemplates"
import LanguageTemplatesIcon from "components/elements/LanguageTemplatesIcon"
import IntegrationsIcon from "components/elements/IntegrationsIcon"
import { eventWalletPassSettingsPath } from "utilities/routes"
import LeadRetrieval from "./LeadRetrieval/LeadRetrieval"
import Integrations from "./Integrations"

type Props = {
  event: DioboxEvent
  updateEvent: (params: {}) => Promise<void>
  cloneEvent: (includeGuests: boolean) => Promise<void>
  deleteEvent: () => Promise<void>
  stripeAccounts: StripeAccount[]
  currentUser: User
  accountUsers?: AccountUser[]
}

const Settings = ({
  event,
  updateEvent,
  cloneEvent,
  deleteEvent,
  stripeAccounts,
  currentUser,
  accountUsers,
}: Props) => {
  const { path } = useRouteMatch()
  const {
    leadRetrieval: leadRetrievalFeatureFlagEnabled,
    badgeBuilder: badgeBuilderFeatureFlagEnabled,
    kioskMode: kioskModeFeatureFlagEnabled,
    walletPass: walletPassFeatureFlagEnabled,
    integrations: integrationsFeatureFlagEnabled,
  } = useFeatureFlag()
  const { temporalQRCodesEnabled } = useFeatures()

  const leadRetrievalEnabled = event.leadRetrievalEnabled || leadRetrievalFeatureFlagEnabled

  if (!accountUsers) {
    return null
  }

  return (
    <SettingsContent
      path={path}
      event={event}
      updateEvent={updateEvent}
      cloneEvent={cloneEvent}
      deleteEvent={deleteEvent}
      stripeAccounts={stripeAccounts}
      currentUser={currentUser}
      accountUsers={accountUsers}
      leadRetrievalEnabled={leadRetrievalEnabled}
      badgeBuilderFeatureFlagEnabled={badgeBuilderFeatureFlagEnabled}
      kioskModeFeatureFlagEnabled={kioskModeFeatureFlagEnabled}
      walletPassFeatureFlagEnabled={walletPassFeatureFlagEnabled}
      integrationsFeatureFlagEnabled={integrationsFeatureFlagEnabled}
      temporalQRCodesEnabled={temporalQRCodesEnabled}
    />
  )
}

type SettingsContentProps = Omit<Props, "accountUsers"> & {
  path: string
  accountUsers: AccountUser[]
  leadRetrievalEnabled: boolean
  badgeBuilderFeatureFlagEnabled: boolean
  kioskModeFeatureFlagEnabled: boolean
  walletPassFeatureFlagEnabled: boolean
  integrationsFeatureFlagEnabled: boolean
  temporalQRCodesEnabled: boolean
}

const SettingsContent = ({
  path,
  event,
  updateEvent,
  cloneEvent,
  deleteEvent,
  stripeAccounts,
  currentUser,
  accountUsers,
  leadRetrievalEnabled,
  badgeBuilderFeatureFlagEnabled,
  kioskModeFeatureFlagEnabled,
  walletPassFeatureFlagEnabled,
  integrationsFeatureFlagEnabled,
  temporalQRCodesEnabled,
}: SettingsContentProps) => (
  <Flex flexGrow={1} position="relative" h="100%">
    <SecondaryNav
      routeMatch="/events/:id/settings"
      items={[
        { icon: "settings-general", color: "blue.600", text: "General" },
        { icon: "settings-emails", color: "blue.400", text: "Email Notifications" },
        { icon: "settings-payments", color: "green.600", text: "Ticketing & Payments" },
        { iconElement: <LanguageTemplatesIcon boxSize={8} color="#7ED321" />, text: "Language Templates" },
        { icon: "settings-custom-domain", color: "teal.300", text: "Custom Email Domains" },
        { icon: "settings-check-in-sessions", color: "pink.500", text: "Event Activities" },
        { icon: "settings-qr-codes", color: "orange.700", text: "QR Codes" },
        ...(kioskModeFeatureFlagEnabled ? [{ icon: "kiosk", color: "purple.700", text: "Kiosk Mode" }] : []),
        ...(walletPassFeatureFlagEnabled ? [{ icon: "wallet-pass", color: "red.500", text: "Wallet Pass" }] : []),
        { icon: "settings-embed-code", color: "orange.300", text: "Embed Code" },
        ...(leadRetrievalEnabled ? [{ icon: "lead-retrieval", color: "blue.300", text: "Lead Retrieval" }] : []),
        ...(!badgeBuilderFeatureFlagEnabled ? [{ icon: "settings-badge-printing", color: "purple.700", text: "Badge Printing" }] : []),
        { icon: "settings-team1", color: "pink.600", text: "Team" },
        ...(integrationsFeatureFlagEnabled ? [{ iconElement: <IntegrationsIcon boxSize={8} color="gray.700" />, text: "Integrations" }] : []),
      ]}
    />
    <CenteredContent>
      <Switch>
        <Route path={`${path}/email-notifications`} exact>
          <EmailNotifications
            event={event}
            teamMembers={accountUsers}
            updateEvent={updateEvent}
          />
        </Route>
        <Route path={`${path}/ticketing-payments`} exact>
          <TicketingPayments
            eventId={event.id}
            currency={event.currency}
            currencySelectDisabled={event.counts.ticketOrders > 0}
            stripeAccounts={stripeAccounts}
            stripeAccountId={event.stripeAccountId || ""}
            updateEvent={updateEvent}
          />
        </Route>
        <Route path={`${path}/language-templates`}>
          <LanguageTemplates event={event} updateEvent={updateEvent} />
        </Route>
        <Route path={`${path}/custom-email-domains`} exact>
          <CustomDomains
            event={event}
            updateEvent={updateEvent}
            enabled={event.whiteLabel}
          />
        </Route>
        <Route path={`${path}/embed-code`} exact>
          <EmbedCode event={event} />
        </Route>
        {integrationsFeatureFlagEnabled && (
          <Route path={`${path}/integrations`} exact>
            <Integrations event={event} />
          </Route>
        )}
        <Route path={`${path}/event-activities`} exact>
          <EventActivities
            checkInLocations={event.checkInLocations}
            enabled={event.checkInLocationsEnabled}
          />
        </Route>
        <Route path={`${path}/qr-codes`} exact>
          <QRCodes
            qrCodesEnabled={event.qrCodesEnabled}
            temporalQRCodesEnabled={temporalQRCodesEnabled}
            expiringQRCodes={event.expiringQrCodes}
            eventId={event.id}
            updateEvent={updateEvent}
          />
        </Route>
        {!badgeBuilderFeatureFlagEnabled && (
          <Route path={`${path}/badge-printing`}>
            <BadgePrinting
              enabled={event.badgePrintingEnabled}
            />
          </Route>
        )}
        <Route path={`${path}/team`} exact>
          <TeamMembers
            currentEvent={event}
            maxTeamSize={event.maxTeamSize}
            currentUser={currentUser}
            accountUsers={accountUsers}
          />
        </Route>
        {leadRetrievalEnabled && (
          <Route path={`${path}/lead-retrieval`} exact>
            <LeadRetrieval event={event} />
          </Route>
        )}
        {kioskModeFeatureFlagEnabled && (
          <Route path={`${path}/kiosk-mode`} exact>
            <KioskMode
              kioskModeEnabled={event.kioskModeEnabled}
              event={event}
              updateEvent={updateEvent}
            />
          </Route>
        )}
        {walletPassFeatureFlagEnabled && (
          <Route path={`${path}/wallet-pass/builder`}>
            <WalletPassBuilder
              event={event}
              updateEvent={updateEvent}
              backPath={eventWalletPassSettingsPath(event.id)}
            />
          </Route>
        )}
        {walletPassFeatureFlagEnabled && (
          <Route path={`${path}/wallet-pass`} exact>
            <WalletPass event={event} />
          </Route>
        )}
        <Route path="">
          <General
            event={event}
            updateEvent={updateEvent}
            cloneEvent={cloneEvent}
            deleteEvent={deleteEvent}
          />
        </Route>
      </Switch>
    </CenteredContent>
  </Flex>
)

export default Settings
